// Visor de la factura en el celular (2026-11-01): la muestra a pantalla completa dentro de la app y la
// comparte (WhatsApp, correo…) o la descarga. El PDF lo da la API del vendedor solo si el documento es
// de un cliente que puede ver; sin señal no hay PDF.
import { useEffect, useRef, useState } from 'react';
import { ErrorApi, guardarArchivo, pedirArchivo } from '../api/cliente';
import { dibujarPdf } from './dibujarPdf';

export interface DocumentoPdf {
  /** Ruta de la API que entrega el PDF (`/vendedor/clientes/…/documentos/…/pdf`). */
  ruta: string;
  empresaId: string;
  /** «Factura N° 5123». */
  titulo: string;
  /** Nombre del archivo al compartir o descargar. */
  archivo: string;
}

const ZOOM = [100, 150, 200];

function mensaje(e: unknown): string {
  if (e instanceof ErrorApi && e.status === 0) return 'Sin señal: el PDF se ve solo con conexión.';
  if (e instanceof ErrorApi && e.status === 404)
    return 'El PDF de este documento todavía no está listo. Intenta de nuevo en un minuto.';
  return e instanceof Error ? e.message : String(e);
}

export function VisorPdf({ doc, alCerrar }: { doc: DocumentoPdf; alCerrar: () => void }) {
  const hoja = useRef<HTMLDivElement>(null);
  const [pdf, setPdf] = useState<Blob | null>(null);
  const [listo, setListo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(0);
  const [aviso, setAviso] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    pedirArchivo('GET', doc.ruta, { query: { empresaId: doc.empresaId } })
      .then((b) => vivo && setPdf(b))
      .catch((e: unknown) => vivo && setError(mensaje(e)));
    return () => {
      vivo = false;
    };
  }, [doc.ruta, doc.empresaId]);

  useEffect(() => {
    const destino = hoja.current;
    if (!pdf || !destino) return;
    let cancelado = false;
    dibujarPdf(pdf, destino, () => cancelado)
      .then(() => !cancelado && setListo(true))
      .catch((e: unknown) => !cancelado && setError(`No se pudo mostrar el PDF: ${mensaje(e)}`));
    return () => {
      cancelado = true;
      destino.replaceChildren();
    };
  }, [pdf]);

  // Mientras está abierto, la página de atrás no se mueve; Escape cierra (en el computador).
  useEffect(() => {
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const tecla = (e: KeyboardEvent) => e.key === 'Escape' && alCerrar();
    window.addEventListener('keydown', tecla);
    return () => {
      document.body.style.overflow = antes;
      window.removeEventListener('keydown', tecla);
    };
  }, [alCerrar]);

  const archivo = pdf ? new File([pdf], doc.archivo, { type: 'application/pdf' }) : null;
  const puedeCompartir =
    !!archivo &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare({ files: [archivo] });

  const compartir = async () => {
    if (!archivo) return;
    setAviso(null);
    try {
      await navigator.share({ files: [archivo], title: doc.titulo });
    } catch (e) {
      // Cerrar el menú de compartir no es un error.
      if ((e as Error).name !== 'AbortError')
        setAviso(`No se pudo compartir: ${(e as Error).message}`);
    }
  };

  return (
    <div className="visor" role="dialog" aria-modal="true" aria-label={doc.titulo}>
      <div className="visor-barra">
        <button className="btn sec sm" type="button" onClick={alCerrar}>
          ✕ Cerrar
        </button>
        <div className="t">{doc.titulo}</div>
        {listo && (
          <button
            className="btn sec sm"
            type="button"
            aria-label={zoom < ZOOM.length - 1 ? 'Ampliar' : 'Ver completo'}
            onClick={() => setZoom((z) => (z + 1) % ZOOM.length)}
          >
            {zoom < ZOOM.length - 1 ? '＋' : '－'}
          </button>
        )}
      </div>
      <div className="visor-cuerpo">
        {error ? (
          <div className="error" style={{ margin: 16 }}>
            {error}
          </div>
        ) : (
          !listo && <div className="vacio">Cargando el PDF…</div>
        )}
        <div ref={hoja} className="visor-hoja" style={{ width: `${ZOOM[zoom]}%` }} />
      </div>
      {pdf && !error && (
        <div className="visor-acc">
          {aviso && <div className="error">{aviso}</div>}
          <div className="botones">
            {puedeCompartir && (
              <button className="btn verde" type="button" onClick={() => void compartir()}>
                Compartir
              </button>
            )}
            <button
              className="btn sec"
              type="button"
              onClick={() => guardarArchivo(pdf, doc.archivo)}
            >
              Descargar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
